import { components, extractRuns, loneCells } from './grid.js';
import type { Grid } from './grid.js';
import type { Dictionary } from './dictionary.js';
import { resolveBlanks } from './blanks.js';
import {
  parseKey, type CoordKey, type GridProblem, type GridReport, type Letter,
  type RunReport, type RunStatus,
} from './types.js';
import { faceOf } from './tiles.js';

export interface ValidateOptions {
  minWordLength: number;
  /**
   * When false the report records `unknown` instead of `invalid` for words the
   * dictionary rejects. Used before the word pack finishes loading, and in
   * peek-mode `off` where the player is not entitled to a verdict.
   */
  decide: boolean;
}

export const DEFAULT_VALIDATE: ValidateOptions = { minWordLength: 2, decide: true };

/**
 * Full legality report for one seat's grid.
 *
 * This is the single source of truth for "can this player declare". The
 * client runs it continuously for its own display and the server runs the very
 * same function before honouring a call, so an edited client cannot talk its
 * way past the rules.
 */
export function validateGrid(
  grid: Grid,
  dict: Dictionary,
  options: ValidateOptions = DEFAULT_VALIDATE,
): GridReport {
  const problems: GridProblem[] = [];
  let score = 0;
  for (const tile of grid.values()) score += tile.value;

  if (grid.size === 0) {
    return {
      runs: [], problems: [{ kind: 'empty' }], complete: false, score: 0,
      tileCount: 0, inferred: new Map(),
    };
  }

  /*
   * 1. Blanks.
   *
   * A blank is allowed to stay a blank. If there is any letter that makes
   * every word through it real, the square is fine and the engine says which
   * letter it took it to be; if there is no such letter, *that* is the
   * problem, and it is a different and more useful thing to say than "this
   * blank has not been filled in".
   *
   * Only when the word list may be consulted, though. Working it out means
   * asking the dictionary, and under a rationed check policy the answer is
   * exactly what a player has not yet earned — so with `decide` false the
   * blanks stay open and their runs stay unjudged, as they did before.
   */
  const unassigned = [...grid.entries()]
    .filter(([, t]) => t.isBlank && t.assigned === null)
    .map(([k]) => k);
  let inferred: ReadonlyMap<CoordKey, Letter> = new Map();
  let effective = grid;
  if (unassigned.length > 0) {
    if (!options.decide) {
      problems.push({ kind: 'unassignedBlank', cells: unassigned.map(parseKey) });
    } else {
      const solved = resolveBlanks(grid, dict, options.minWordLength);
      if (solved.grid) {
        effective = solved.grid;
        inferred = solved.inferred;
      } else {
        problems.push({ kind: 'blankNoFit', cells: unassigned.map(parseKey) });
      }
    }
  }

  // 2. One connected component.
  const comps = components(grid);
  if (comps.length > 1) {
    const stray = comps.slice(1).flat().map(parseKey);
    problems.push({ kind: 'disconnected', components: comps.length, strayCells: stray });
  }

  // 3. Every tile must take part in at least one word.
  const lone = loneCells(grid);
  if (lone.length) problems.push({ kind: 'loneTile', cells: lone });

  // 4. Every maximal run must be a word. Read from the grid with the blanks
  // standing for something, where that was possible.
  const runs: RunReport[] = [];
  for (const run of extractRuns(effective)) {
    let status: RunStatus;
    if (run.word.includes('?')) {
      status = 'incomplete';
    } else if (run.cells.length < options.minWordLength) {
      status = 'invalid';
    } else if (!options.decide) {
      status = 'unknown';
    } else {
      status = dict.has(run.word) ? 'valid' : 'invalid';
    }
    runs.push({ ...run, status });
    if (status === 'invalid') problems.push({ kind: 'invalidWord', run });
    else if (status === 'unknown') problems.push({ kind: 'unknownWord', run });
  }

  return {
    runs,
    problems,
    complete: problems.length === 0 && runs.length > 0,
    score,
    tileCount: grid.size,
    inferred,
  };
}

/** True when the seat may declare: grid legal and rack empty. */
export function canDeclare(
  grid: Grid,
  rackSize: number,
  dict: Dictionary,
  options: ValidateOptions = DEFAULT_VALIDATE,
): { ok: boolean; report: GridReport; reason?: string } {
  const report = validateGrid(grid, dict, options);
  if (rackSize > 0) {
    return { ok: false, report, reason: `${rackSize} tile${rackSize === 1 ? '' : 's'} still in hand` };
  }
  if (!report.complete) return { ok: false, report, reason: describeFirstProblem(report) };
  return { ok: true, report };
}

/** A short, human sentence for the most pressing problem on a grid. */
export function describeFirstProblem(report: GridReport): string | undefined {
  const p = report.problems[0];
  if (!p) return undefined;
  switch (p.kind) {
    case 'empty': return 'The grid is empty.';
    case 'disconnected':
      return `The grid is in ${p.components} separate pieces — everything has to join up.`;
    case 'loneTile':
      return `${p.cells.length} tile${p.cells.length === 1 ? ' is' : 's are'} not part of any word.`;
    case 'unassignedBlank':
      return 'A blank tile still needs a letter.';
    case 'blankNoFit':
      return p.cells.length === 1
        ? 'There is no letter that would make that blank work.'
        : `There is no set of letters that would make those ${p.cells.length} blanks work.`;
    case 'invalidWord': return `"${titleCase(p.run.word)}" is not in the word list.`;
    case 'unknownWord': return 'The word list is still loading.';
  }
}

const titleCase = (w: string) => w.charAt(0) + w.slice(1).toLowerCase();

/** Resolved text of a run, for display and definition lookup. */
export function wordText(cells: readonly { x: number; y: number }[], grid: Grid): string {
  let out = '';
  for (const c of cells) {
    const t = grid.get(`${c.x},${c.y}`);
    out += t ? (faceOf(t) ?? '?') : '?';
  }
  return out;
}
